import React, { useState, useEffect, useRef } from 'react';
import { DollarSign, Percent } from 'lucide-react';

const LoanStructure = ({ formData, setFormData }) => {
  const [inputType, setInputType] = useState('percentage');
  const [percentageInputs, setPercentageInputs] = useState({});
  const [dollarInputs, setDollarInputs] = useState({});
  const totalAmount = parseFloat(formData.loanSize) || 0;
  const inputRefs = useRef({});

  useEffect(() => {
    const initialPercentageInputs = {};
    const initialDollarInputs = {};
    Object.entries(formData.loanStructure || {}).forEach(([key, value]) => {
      if (key === 'equityInjection') {
        Object.entries(value).forEach(([subKey, subValue]) => {
          const fieldName = `${key}.${subKey}`;
          initialPercentageInputs[fieldName] = subValue;
          initialDollarInputs[fieldName] = formatDollarAmount(((parseFloat(subValue) || 0) / 100 * totalAmount).toFixed(2));
        });
      } else {
        initialPercentageInputs[key] = value;
        initialDollarInputs[key] = formatDollarAmount(((parseFloat(value) || 0) / 100 * totalAmount).toFixed(2));
      }
    });
    setPercentageInputs(initialPercentageInputs);
    setDollarInputs(initialDollarInputs);
  }, [formData.loanStructure, totalAmount]);

  const formatDollarAmount = (value) => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const handleInputChange = (e, field) => {
    const value = e.target.value.replace(/,/g, '');
    const cursorPosition = e.target.selectionStart;

    if (e.key === 'Enter') {
      e.preventDefault();
      return;
    }

    if (inputType === 'percentage') {
      setPercentageInputs(prev => ({ ...prev, [field]: value }));
      const percentage = parseFloat(value) || 0;
      setDollarInputs(prev => ({ ...prev, [field]: formatDollarAmount(Math.round(percentage / 100 * totalAmount)) }));
    } else {
      const dollars = parseInt(value) || 0;
      setDollarInputs(prev => ({ ...prev, [field]: formatDollarAmount(dollars) }));
      const percentage = totalAmount > 0 ? ((dollars / totalAmount) * 100).toFixed(2) : '0';
      setPercentageInputs(prev => ({ ...prev, [field]: percentage }));
    }

    setTimeout(() => {
      const input = inputRefs.current[field];
      if (input) {
        input.setSelectionRange(cursorPosition, cursorPosition);
      }
    }, 0);
  };

  const saveToFirebase = () => {
    const newLoanStructure = Object.entries(percentageInputs).reduce((acc, [key, value]) => {
      if (key.includes('.')) {
        const [main, sub] = key.split('.');
        if (!acc[main]) acc[main] = {};
        acc[main][sub] = value === '' ? '' : Math.round(parseFloat(value)).toString();
      } else {
        acc[key] = value === '' ? '' : Math.round(parseFloat(value)).toString();
      }
      return acc;
    }, {});

    setFormData(prev => ({
      ...prev,
      loanStructure: newLoanStructure
    }));
  };

  const renderInput = (label, field) => (
    <div key={field}>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <div className="relative">
        {inputType === 'dollar' && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">$</span>
        )}
        <input
          ref={el => inputRefs.current[field] = el}
          type="text"
          value={inputType === 'percentage' ? percentageInputs[field] || '' : dollarInputs[field] || ''}
          onChange={(e) => handleInputChange(e, field)}
          onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
          onBlur={saveToFirebase}
          placeholder={inputType === 'percentage' ? '0' : 'Amount'}
          className={`w-full py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
            inputType === 'dollar' ? 'pl-7 pr-3' : 'pl-3 pr-8'
          }`}
        />
        {inputType === 'percentage' && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">%</span>
        )}
      </div>
    </div>
  );

  const totalPercentage = () => {
    return Object.values(percentageInputs).reduce((sum, value) => sum + (parseInt(value) || 0), 0);
  };

  const isCloseToHundred = (value) => {
    return Math.abs(value - 100) < 0.01;
  };

  const total = totalPercentage();
  const equityTotal = (parseInt(percentageInputs['equityInjection.personal']) || 0) + (parseInt(percentageInputs['equityInjection.fullStandby']) || 0);

  return (
    <div className="space-y-3">
      {/* %/$ Toggle (mirrors broker's contact-preference toggle) */}
      <div className="relative inline-flex w-full p-1 bg-gray-100 rounded-xl">
        <span
          className="absolute top-1 bottom-1 left-1 bg-white rounded-lg shadow-sm transition-transform duration-200 ease-out"
          style={{
            width: 'calc(50% - 4px)',
            transform: inputType === 'percentage' ? 'translateX(0)' : 'translateX(100%)',
          }}
        />
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); setInputType('percentage'); }}
          className={`relative flex-1 z-10 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1 ${
            inputType === 'percentage' ? 'text-indigo-700' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Percent size={12} />
          Percentage
        </button>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); setInputType('dollar'); }}
          className={`relative flex-1 z-10 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1 ${
            inputType === 'dollar' ? 'text-indigo-700' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <DollarSign size={12} />
          Dollar
        </button>
      </div>

      <div className="space-y-2">
        {renderInput('SBA 7(a) Loan', 'sba7aLoan')}
        {renderInput('Conventional Loan', 'conventionalLoan')}
        {renderInput('Non-Full Standby Seller Note', 'sellersNote')}
        {renderInput('Personal Equity Injection', 'equityInjection.personal')}
        {renderInput('Full Standby Seller Note', 'equityInjection.fullStandby')}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
        <span className="text-xs font-medium text-gray-600">Total</span>
        <span className={`text-sm font-semibold ${isCloseToHundred(total) ? 'text-emerald-600' : 'text-orange-600'}`}>
          {inputType === 'percentage'
            ? `${total}%`
            : `$${formatDollarAmount(Object.values(dollarInputs).reduce((sum, value) => sum + (parseInt(value.replace(/,/g, '')) || 0), 0))}`}
          {!isCloseToHundred(total) && <span className="ml-2 text-xs font-normal text-orange-500">(must total 100%)</span>}
        </span>
      </div>

      {equityTotal > 0 && equityTotal < 10 && (
        <p className="text-xs text-orange-600">Combined Equity Injection must be at least 10%.</p>
      )}
    </div>
  );
};

export default LoanStructure;
